/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';

import { useRouter } from 'libs/router/useRouter';
import { defaultLocale, languageNames, Languages } from 'libs/locale';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Icon } from 'ui/atoms/Icon/Icon';
import { Flag } from 'ui/atoms/Flag/Flag';
import { Dropdown } from 'ui/atoms/Dropdown/Dropdown';

import styles from './LanguageDropdown.module.scss';

const baseClass = 'language-dropdown';

const languageFlags: Partial<Record<string, string>> = {
  en: 'gb',
};

interface Props {
  position?: 'right' | 'left' | 'top';
}

export const LanguageDropdown = ({ position = 'right' }: Props) => {
  const { links } = useContext(ContentContext);
  const { t } = useInt();
  const router = useRouter();

  const [lang, setLang] = useState<Languages>(defaultLocale);

  useEffect(() => {
    setLang(`${router.query.lang}` as Languages);
  }, [router.query]);

  const options = useMemo(
    () =>
      links
        .filter((item, index) => item.key && links.findIndex((l) => l.key === item.key) === index)
        .map((item) => ({
          value: item.key,
          title: (
            <a
              {...(item.link
                ? { href: `${process.env.HOST || ''}${item.link}` }
                : { href: `${process.env.HOST || ''}/` })}
              className={styles[`${baseClass}-option`]}
            >
              <Flag code={languageFlags[item.key] ?? item.key} rounded />
              <span>{languageNames[item.key as Languages]}</span>
              <Icon type="arrow-3" className={`${styles[`${baseClass}-option-arrow`]} ${baseClass}-option-arrow`} />
            </a>
          ),
        })),
    [links],
  );

  const onChange = useCallback((value: string) => {
    setLang(value as Languages);
    document.cookie = `NEXT_LOCALE=${value}`;
  }, []);

  return (
    <Dropdown
      title={t('Your Language')}
      position={position}
      className={styles[`${baseClass}-wrapper`]}
      onChange={onChange}
      options={options}
      value={lang}
      label={(isOpen) => (
        <span className={cn(styles[`${baseClass}-label`], isOpen && styles[`${baseClass}-label--open`])}>
          <Icon type="global" />
          <span>{lang.toUpperCase()}</span>
        </span>
      )}
    />
  );
};
